/**
 * artifactState — 채팅 요청에 동봉하는 학습지/PPT 산출물 현황 요약.
 *
 * chatApi.sendMessageStream body 의 `artifact_state` 필드 모양(백엔드 계약):
 *   {
 *     has_artifact: boolean,
 *     latest: { format, pageCount, filename, createdAt, sourceMode, studentPageCount? } | null,
 *     available_formats: string[],
 *     mode: 'text' | 'image' | null,
 *     modes_available: ['text', 'image'],
 *     layout_hint?: { columns, paper_style, font_scale, density, target_pages,
 *                     pages_actual, fit_result, figure_slots },
 *     pages_actual?: number,
 *   }
 *     kind?: 'ppt' | 'worksheet',   // P1: 헤더 카테고리와 무관하게 "어느 산출물의 상태인지"
 *     image_stale?: true,           // 이미지 버전이 지금 초안보다 예전 내용(편집 전)일 때만
 *   }
 * category(=활성 산출물 카테고리)가 없으면 null 을 돌려주고, 호출자는 필드를 생략한다.
 * ChatPage 를 건드리지 않고 chatApi 가 existing_outline 으로 layout_hint 를 붙인다.
 */

import { pickHintForState } from './worksheetLayoutKnobs';

const ARTIFACT_CATEGORIES = new Set(['ppt_maker', 'worksheet_gen']);
const KIND_TO_CATEGORY = Object.freeze({ ppt: 'ppt_maker', worksheet: 'worksheet_gen' });
const CATEGORY_TO_KIND = Object.freeze({ ppt_maker: 'ppt', worksheet_gen: 'worksheet' });

export const artifactKindToCategory = (kind) => KIND_TO_CATEGORY[kind] || null;
export const artifactCategoryToKind = (category) => CATEGORY_TO_KIND[category] || null;

/**
 * P1: 활성 산출물 kind (파생값, 저장하지 않음).
 *  1) 헤더가 PPT/학습지이고 그 산출물이 있으면 그것
 *  2) 한 종류만 있으면 그것
 *  3) 둘 다 있으면 마지막으로 만들거나 연 종류(lastTouched), 없으면 헤더 쪽, 그것도 아니면 ppt
 *  4) 산출물이 없으면 null
 */
export const resolveActiveArtifactKind = ({
  selectedCategory,
  hasPpt = false,
  hasWorksheet = false,
  lastTouched = null,
} = {}) => {
  const headerKind = artifactCategoryToKind(selectedCategory);
  if (headerKind === 'ppt' && hasPpt) return 'ppt';
  if (headerKind === 'worksheet' && hasWorksheet) return 'worksheet';
  if (hasPpt && !hasWorksheet) return 'ppt';
  if (hasWorksheet && !hasPpt) return 'worksheet';
  if (hasPpt && hasWorksheet) {
    if (lastTouched === 'ppt' || lastTouched === 'worksheet') return lastTouched;
    return headerKind || 'ppt';
  }
  return null;
};

const toMode = (value) => (value === 'text' || value === 'image' ? value : null);

const integerPageCount = (value) => {
  if (value == null || typeof value === 'boolean') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(number) && number >= 0 ? number : null;
};

const TEXT_DECK_BUSY = new Set(['generating', 'restyling', 'resuming', 'restyle_queued']);

/**
 * 텍스트형 PPT(덱) 현황 — 덱은 결과 기록(pptArtifacts)에 들어가지 않아, 따로 알려 주지 않으면
 * AI 가 "아직 파일이 없어요"라고 답한다.
 *   { exists, slide_count?, minutes_since_made?, busy?: 'generating'|'restyling'|'resuming'|'restyle_queued' }
 */
export const buildTextDeckState = (outline, { activity = null, restyleQueued = false, now = Date.now() } = {}) => {
  const ref = outline && typeof outline === 'object' ? outline.free_design : null;
  const hasDeck = Boolean(ref && typeof ref === 'object' && ref.deck_id);
  const busy = TEXT_DECK_BUSY.has(activity) ? activity : (restyleQueued ? 'restyle_queued' : null);
  if (!hasDeck && !busy) return null;
  const state = { exists: hasDeck };
  if (hasDeck) {
    const count = Number(ref.slide_count);
    if (Number.isInteger(count) && count > 0) state.slide_count = count;
    const madeMs = Date.parse(ref.created_at || '');
    if (Number.isFinite(madeMs)) state.minutes_since_made = Math.max(0, Math.round((now - madeMs) / 60000));
  }
  if (busy) state.busy = busy;
  return state;
};

export const buildArtifactState = ({
  category,
  kind = null,
  artifacts = [],
  availableFormats = [],
  fallbackMode = null,
  outline = null,
  pagesActual = null,
  textDeck = null,
  imageStale = false,
} = {}) => {
  if (!ARTIFACT_CATEGORIES.has(category)) return null;
  const list = Array.isArray(artifacts) ? artifacts.filter(Boolean) : [];
  const latest = list[0] || null;
  const state = {
    has_artifact: list.length > 0,
    latest: latest
      ? {
        format: latest.format || null,
        pageCount: Number.isInteger(latest.pageCount) ? latest.pageCount : 0,
        filename: latest.filename || null,
        createdAt: latest.createdAt || null,
        sourceMode: toMode(latest.sourceMode),
        ...(Number.isInteger(latest.studentPageCount) && latest.studentPageCount > 0
          ? { studentPageCount: latest.studentPageCount }
          : {}),
      }
      : null,
    available_formats: (Array.isArray(availableFormats) ? availableFormats : [])
      .map((format) => String(format).toLowerCase())
      .filter(Boolean),
    mode: toMode(latest?.sourceMode) || toMode(fallbackMode),
    modes_available: ['text', 'image'],
  };
  const resolvedKind = kind || artifactCategoryToKind(category);
  if (resolvedKind) state.kind = resolvedKind;
  if (textDeck && typeof textDeck === 'object') state.text_deck = textDeck;
  if (imageStale === true) state.image_stale = true;
  return attachOutlineLayoutToArtifactState(state, outline, pagesActual);
};

export function attachOutlineLayoutToArtifactState(artifactState, outline, pagesActual = null) {
  if (!artifactState || typeof artifactState !== 'object') return artifactState;
  if (!outline || typeof outline !== 'object') {
    const onlyPages = integerPageCount(pagesActual);
    if (onlyPages == null) return artifactState;
    return { ...artifactState, pages_actual: onlyPages };
  }
  const hint = pickHintForState(outline.layout_hint, outline);
  const actual = integerPageCount(pagesActual)
    ?? integerPageCount(hint.pages_actual)
    ?? integerPageCount(outline.student_page_count)
    ?? integerPageCount(outline.pages_actual);
  if (!Object.keys(hint).length && actual == null) return artifactState;
  const nextHint = { ...hint };
  if (actual != null) nextHint.pages_actual = actual;
  const next = {
    ...artifactState,
    layout_hint: { ...(artifactState.layout_hint || {}), ...nextHint },
  };
  if (actual != null) next.pages_actual = actual;
  return next;
}

const artifactState = {
  buildArtifactState,
  buildTextDeckState,
  attachOutlineLayoutToArtifactState,
  resolveActiveArtifactKind,
  artifactKindToCategory,
  artifactCategoryToKind,
};

export default artifactState;
